import React, { memo, useCallback, useContext, useEffect, useState } from "react";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import ElementObserver from "@swiggy-private/dls-react-mxn/dist/ElementObserver";
import { AnalyticsContext, ObjectNames, TableContext } from "@swiggy-private/portal-dineout-context";
import { BillPaymentHalfCard } from "@swiggy-private/portal-dineout-ui";
import { useRouter } from "next/compat/router";
import { isApp } from "utils";
import { getBridgeSdk } from "utils/bridgeSdk";
import { sendImpressionEvent, sendClickEvent } from "./helpers";
import { IBillPaymentResponse } from "../../hooks/useBillPayment";

export interface BillPaymentHalfCardRendererProps {
    billPaymentData: IBillPaymentResponse;
    onClose: () => void;
}

type IBillPaymentCTA = IBillPaymentResponse["ctas"][number];

const BillPaymentHalfCardRenderer: React.FC<BillPaymentHalfCardRendererProps> = ({ billPaymentData, onClose }) => {
    const router = useRouter();
    const [isVisible, setIsVisible] = useState(true);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const tableContext = useContext(TableContext);

    // Reset visibility when new bill payment data is received
    useEffect(() => {
        if (billPaymentData) {
            setIsVisible(true);
        }
    }, [billPaymentData]);

    const onPageImpression = useCallback(() => {
        sendImpressionEvent({
            eventFn: impressionEvent,
            objectName: ObjectNames.IMPRESSION_BILL_PAYMENT,
            billPaymentData,
            tableContext,
        });
    }, [impressionEvent, billPaymentData, tableContext]);

    const handleClose = useCallback(() => {
        setIsVisible(false);
        onClose();
    }, [onClose]);

    const handleCtaClick = useCallback(
        (cta: IBillPaymentCTA) => {
            sendClickEvent({
                eventFn: clickEvent,
                objectName: ObjectNames.CLICK_BILL_PAYMENT_CTA,
                billPaymentData,
                clickedCTA: cta,
                tableContext,
            });

            switch (cta.type) {
                case NavigationTypes.DEEPLINK:
                    if (!isApp()) {
                        router?.push(cta.link);
                        break;
                    }
                    getBridgeSdk()
                        .navigateToScreen({
                            type: NavigationTypes.DEEPLINK,
                            link: cta.link,
                        })
                        .catch((error) => {
                            console.error("Error navigating to deeplink", error);
                        });
                    break;

                case NavigationTypes.SCREEN:
                    router?.push(cta.link);
                    break;

                default:
                    handleClose();
                    break;
            }
            setIsVisible(false);
        },
        [clickEvent, billPaymentData, handleClose, tableContext, router],
    );

    if (!isVisible) {
        return null;
    }

    const billPaymentCardData = {
        title: billPaymentData.title,
        description: billPaymentData.description,
        ctas: Array.isArray(billPaymentData.ctas) ? billPaymentData.ctas : [],
        paymentIcon: billPaymentData.imageId,
    };

    return (
        <ElementObserver onViewEnter={onPageImpression} senseOnce>
            <BillPaymentHalfCard
                show={isVisible}
                onClose={handleClose}
                onCtaClick={handleCtaClick}
                billPaymentCardData={billPaymentCardData}
            />
        </ElementObserver>
    );
};

export default memo(BillPaymentHalfCardRenderer);
